/**
 * DialogShell — minimal adapter that lets a form pick between the
 * legacy Modal and the W4 WorkspaceDrawer with a `presentation` prop.
 *
 * The two primitives share the same essential props (`open`, `onClose`,
 * `title`, `subtitle`/description, `footer`/`actions`, body children).
 * This adapter exists so forms that previously hard-coded `<Modal>` can
 * migrate to drawers incrementally without forking the form body.
 *
 * Default presentation is 'modal' — every existing call site is
 * unaffected. Pass `presentation='drawer'` from the host page to
 * render the form in a right-side drawer instead.
 */

import React from 'react';
import Modal from './Modal';
import WorkspaceDrawer from './WorkspaceDrawer';

export default function DialogShell({
  open,
  onClose,
  title,
  subtitle = null,
  footer = null,
  children,
  presentation = 'modal',
  size = 'md',
  busy = false,
  initialFocusRef = null,
  'data-testid': testId
}) {
  if (presentation === 'drawer') {
    // Map size → drawer width: md is the default.
    const widthMap = { sm: 'sm', md: 'md', lg: 'lg', xl: 'lg' };
    return (
      <WorkspaceDrawer
        open={open}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        actions={footer}
        busy={busy}
        width={widthMap[size] || 'md'}
        initialFocusRef={initialFocusRef}
        data-testid={testId || 'workspace-drawer'}
      >
        {children}
      </WorkspaceDrawer>
    );
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={subtitle}
      size={size}
      footer={footer}
      initialFocusRef={initialFocusRef}
    >
      {children}
    </Modal>
  );
}
